/**
 * @graphql cc
 */
import { graphql } from 'react-relay';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Grid, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { SmartCreateFeedback, SmartCreateFeedbackVariant } from '@atlassian/smart-create-feedback';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { AILabsLozenge } from '@atlassian/ai-components';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { usePageContentId } from '@confluence/page-context';
import { useCfind4473CreationFocusedNav } from '@confluence/experiment-cfind-4473-creation-focused-nav/entry-points/useCfind4473CreationFocusedNav.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { fg } from '@confluence/feature-gating';

import type {
	SmartCreateFeedbackBannerQuery$data,
	SmartCreateFeedbackBannerQuery$variables,
} from './__generated__/SmartCreateFeedbackBannerQuery.graphql.ts';
import { isBannerDismissed, dismissBanner, permanentlyDismissBanner } from './bannerDismissal.ts';

const feedbackBannerStyles = xcss({
	padding: 'space.050',
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	backgroundColor: 'elevation.surface.sunken',
	color: 'color.text.subtle',
});

const feedbackBannerTopBorderStyles = xcss({
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
});

const feedbackBannerLayerStyles = xcss({
	zIndex: 'modal',
});

const feedbackBannerMessageStyles = xcss({
	gridArea: 'message',
});

const closeButtonStyles = xcss({
	display: 'flex',
	justifySelf: 'end',
	gridArea: 'close',
});

const bannerStyles = xcss({
	width: '100%',
});

const i18n = defineMessages({
	bannerMessage: {
		id: 'editor-action-bar.feedback-banner.message.smart-create',
		defaultMessage:
			'This content was created with Rovo! We’d love to hear what you think. <link>Share feedback</link>',
		description: 'Banner message for the smart create feedback banner',
	},
	bannerClose: {
		id: 'editor-action-bar.feedback-banner.close.smart-create',
		defaultMessage: 'Close',
		description: 'Close button for the smart create feedback banner',
	},
});

const SmartCreateFeedbackBannerQuery = graphql`
	query SmartCreateFeedbackBannerQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			title
			id
			type
			creatorId
			contentProperties {
				draft {
					generatedBy
				}
				latest {
					generatedBy
				}
			}
		}
	}
`;

const SMART_CREATE_GENERATED_BY = 'ROVO_SMART_CREATION';

const SmartCreateFeedbackBannerComponent = ({
	contentType,
}: {
	contentType: 'page' | 'whiteboard';
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [showBanner, setShowBanner] = useState<boolean>(!isBannerDismissed());
	const [showFeedbackCollector, setShowFeedbackCollector] = useState<boolean>(false);
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const [contentId] = usePageContentId();
	const { userId, cloudId } = useSessionData();
	const pageMode = useGetPageMode();
	const contentMode = contentType === 'page' ? pageMode : undefined;

	const { areBannersHidden } = useCfind4473CreationFocusedNav(); // simplify the nav for D0 experience

	const handleOnClickClose = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'smartCreateFeedbackClose',
				source: 'smartCreateFeedbackBanner',
				attributes: {
					contentType,
					contentMode,
				},
			},
		}).fire();
		dismissBanner();
		setShowBanner(false);
	}, [createAnalyticsEvent, contentType, contentMode]);

	const handleOnClickFeedbackButton = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'smartCreateFeedbackShare',
				source: 'smartCreateFeedbackBanner',
				attributes: {
					contentType,
					contentMode,
				},
			},
		}).fire();
		setShowFeedbackCollector(true);
	}, [createAnalyticsEvent, contentType, contentMode]);

	const { data, loading } = useQueryInterop<
		SmartCreateFeedbackBannerQuery$data,
		SmartCreateFeedbackBannerQuery$variables
	>(SmartCreateFeedbackBannerQuery, {
		variables: {
			contentId: contentId ?? '',
		},
		skip: !contentId || !userId || areBannersHidden,
	});

	const { singleContent } = data || {};
	const contentProperties = singleContent?.contentProperties;

	const isGeneratedBySmartCreate =
		contentProperties?.draft?.generatedBy === SMART_CREATE_GENERATED_BY ||
		contentProperties?.latest?.generatedBy === SMART_CREATE_GENERATED_BY;

	const createdByCurrentUser = userId ? singleContent?.creatorId === userId : false;

	const shouldShowFeedbackBanner = isGeneratedBySmartCreate && createdByCurrentUser;

	useEffect(() => {
		if (showBanner && shouldShowFeedbackBanner && !loading && !areBannersHidden) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'smartCreateFeedbackBanner',
					attributes: {
						contentType,
						contentMode,
					},
				},
			}).fire();
		}
	}, [
		showBanner,
		shouldShowFeedbackBanner,
		createAnalyticsEvent,
		contentType,
		contentMode,
		loading,
		areBannersHidden,
	]);

	// D0 experiment for simplifying the first day UX
	if (areBannersHidden || loading || !shouldShowFeedbackBanner) {
		return null;
	}

	return showBanner ? (
		<div data-vc="smart-create-feedback-banner" {...ssrPlaceholderIdProp} css={bannerStyles}>
			<Grid
				templateAreas={['empty message close']}
				templateColumns="1fr auto 1fr"
				xcss={[
					feedbackBannerStyles,
					feedbackBannerLayerStyles,
					(contentMode === 'view' || contentMode === 'live') && feedbackBannerTopBorderStyles,
				]}
				testId="smart-create-feedback-banner"
			>
				<Inline
					space="space.100"
					alignInline="center"
					alignBlock="center"
					xcss={feedbackBannerMessageStyles}
				>
					{!fg('aifc_onboarding_ga_enabled') ? (
						<AILabsLozenge />
					) : (
						!expValEquals('cc-aifc-databases-experiment', 'isEnabled', true) && <AILabsLozenge />
					)}
					<FormattedMessage
						{...i18n.bannerMessage}
						values={{
							link: (chunks: React.ReactNode[]) => (
								<Button appearance="link" spacing="none" onClick={handleOnClickFeedbackButton}>
									{chunks}
								</Button>
							),
						}}
					/>
				</Inline>
				<Box xcss={closeButtonStyles}>
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={CrossIcon}
						label={formatMessage(i18n.bannerClose)}
						onClick={handleOnClickClose}
					/>
				</Box>
			</Grid>

			{showFeedbackCollector && (
				<SmartCreateFeedback
					variant={SmartCreateFeedbackVariant.CREATE_WITH_ROVO}
					onClose={() => setShowFeedbackCollector(false)}
					onSubmit={() => {
						setShowFeedbackCollector(false);
						permanentlyDismissBanner();
					}}
					tenantId={cloudId}
					additionalFields={{
						'Entry Point': 'Confluence SmartCreateFeedbackBanner',
						contentType,
						contentMode,
					}}
				/>
			)}
		</div>
	) : null;
};

export const SmartCreateFeedbackBanner = withFlags(SmartCreateFeedbackBannerComponent);
